import React from 'react';
import { Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';

import Modal from './Modal';

import { AlertTypes } from '../constants';
import { T, FONT_MONO, ALERT_DETAILS } from '../constants/tokens';

/**
 * Rows need hover and focus, which an inline style cannot carry. Choosing an
 * alert is consequential enough that the row should visibly respond before
 * it is clicked.
 */
const useStyles = makeStyles({
  row: {
    transition: 'background 120ms ease, border-color 120ms ease',
    '&:hover': {
      background: T.surfaceSoft,
      borderColor: T.muted
    },
    '&:focus-visible': {
      outline: '2px solid ' + T.ink,
      outlineOffset: 2
    }
  }
});

/**
 * "Set an alert type" — the picker behind the header's status control.
 *
 * Rows are built from `AlertTypes`, not from a hardcoded list, so the picker
 * shows exactly the statuses the server accepts. Secure, Evacuate and Shelter
 * are described in ALERT_DETAILS but have no AlertType row yet, so they do not
 * appear; when the migration adding them lands they show up here with no
 * change to this file.
 */
export default function AlertPicker({ open, onClose, onSelect }) {
  const classes = useStyles();
  const rows = Object.keys(ALERT_DETAILS)
    .filter((key) => AlertTypes[key] != null)
    .map((key) => ({ key, status: AlertTypes[key], ...ALERT_DETAILS[key] }));

  const srp = rows.filter((row) => row.group === 'srp');
  const other = rows.filter((row) => row.group === 'other');

  const sectionLabel = (text) => (
    <div
      style={{
        fontFamily: FONT_MONO,
        fontSize: 12,
        fontWeight: 500,
        lineHeight: '16px',
        textTransform: 'uppercase',
        color: T.body,
        marginBottom: 12
      }}
    >
      {text}
    </div>
  );

  const row = (item) => (
    <button
      key={item.key}
      type='button'
      onClick={() => onSelect(item.status)}
      className={classes.row}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        textAlign: 'left',
        fontFamily: 'inherit',
        padding: 16,
        border: '1px solid ' + T.hairline,
        borderRadius: 6,
        background: T.elevated,
        cursor: 'pointer'
      }}
    >
      <span
        style={{
          width: 16,
          height: 16,
          borderRadius: 9999,
          flex: 'none',
          background: item.status.getColor().main,
          boxShadow: '0 0 0 1px rgba(35,37,29,0.12)'
        }}
      />
      <span style={{ flex: '1 1 160px', minWidth: 0 }}>
        <span
          style={{
            display: 'block',
            fontSize: 16,
            fontWeight: 500,
            lineHeight: '24px',
            letterSpacing: '-0.32px',
            color: T.ink
          }}
        >
          {item.status.getDashboardStatus()}
        </span>
        <span style={{ display: 'block', fontFamily: FONT_MONO, fontSize: 12, color: T.body }}>
          Badge LCD · {item.message}
        </span>
      </span>
      <span
        style={{
          flex: '1 1 150px',
          minWidth: 0,
          textAlign: 'right',
          fontSize: 14,
          lineHeight: '20px',
          color: T.body,
          textWrap: 'pretty'
        }}
      >
        {item.action}
      </span>
    </button>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={560}
      eyebrow='Alert types'
      title='Set an alert type'
      body='This alerts every badge in the school. Choose the response you want staff to take.'
      actions={<Button variant='outlined' onClick={onClose}>Cancel</Button>}
    >
      {srp.length > 0 &&
        <>
          {sectionLabel('Standard response protocol')}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
            {srp.map(row)}
          </div>
        </>}

      {other.length > 0 &&
        <>
          {sectionLabel('Other alert types')}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
            {other.map(row)}
          </div>
        </>}
    </Modal>
  );
}

AlertPicker.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired
};
